import React, { useState } from 'react';
import DateTimePicker from '../../components/DateTimePicker';
import BillToLookupField, { type BillToValue } from '../../components/BillToLookupField';
import type { InvoiceListParams } from '../../services/invoiceList.service';
import { InvoiceListContent } from './InvoiceList';
import { calendarDate, dateParam, REPORT_BUTTON } from './reports/reportUtils';

export const INVOICE_SEARCH_TITLE = 'Search for Invoice / Master Pro';
const INPUT = 'mt-1 w-full rounded border border-theme-border-primary bg-theme-bg-primary px-2 py-1.5 text-sm text-label';

/** V1 dashboard_accounting.pl::dash_Acct_BillToSearch -> dosetlistinvoice.
 * Typed Bill To names search by prefix; a lookup selection also narrows by recno. */
export function InvoiceSearchForm({ onSearch }: { onSearch: (params: InvoiceListParams) => void }) {
  const [invoice, setInvoice] = useState('');
  const [masterpro, setMasterpro] = useState('');
  const [billto, setBillto] = useState<BillToValue>({ recno: 0, name: '' });
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [error, setError] = useState('');
  const useDates = !invoice.trim() && !masterpro.trim() && !!billto.name.trim();
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const invoicenum = invoice.trim().toUpperCase();
    const master = masterpro.trim().toUpperCase();
    const billtoname = billto.name.trim().toUpperCase();
    if (!invoicenum && !master && !billtoname) { setError('Enter an invoice number, Master Pro, or Bill To name.'); return; }
    if (useDates && end && !start) { setError('Enter a start date when using an end date.'); return; }
    if (useDates && start && end && dateParam(end) < dateParam(start)) { setError('End date must be on or after start date.'); return; }
    setError('');
    // V1 resolves Invoice # first. Dates apply only to the Bill To-only arm.
    onSearch(invoicenum ? { invoicenum } : {
      ...(master ? { masterpro: master } : {}), ...(billtoname ? { billtoname } : {}),
      ...(billto.recno > 0 ? { billto: billto.recno } : {}),
      ...(useDates && start ? { invoicestartdate: dateParam(start), invoiceenddate: dateParam(end ?? start) } : {}),
    });
  };
  return <form onSubmit={submit} className="p-3 space-y-3 text-label">
    <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
      <label className="min-w-0 text-sm">Invoice #<input aria-label="Invoice #" value={invoice} onChange={e => setInvoice(e.target.value)} onBlur={() => setInvoice(v => v.trim().toUpperCase())} className={INPUT} /></label>
      <span className="pb-2 text-sm text-muted">or</span>
      <label className="min-w-0 text-sm">Master Pro<input aria-label="Master Pro" value={masterpro} onChange={e => setMasterpro(e.target.value)} onBlur={() => setMasterpro(v => v.trim().toUpperCase())} className={INPUT} /></label>
    </div>
    <BillToLookupField value={billto} onChange={setBillto} />
    <div className="grid grid-cols-2 gap-3">
      <DateTimePicker mode="date" labelType="custom" label="Invoice Date From" ariaLabel="Invoice Date From" value={start} onChange={v => setStart(calendarDate(v))} disabled={!useDates} usePortal autoFlip />
      <DateTimePicker mode="date" labelType="custom" label="Invoice Date To" ariaLabel="Invoice Date To" value={end} onChange={v => setEnd(calendarDate(v))} disabled={!useDates} usePortal autoFlip />
    </div>
    <p className="text-xs text-muted">Invoice # takes priority. Dates apply to Bill To-only searches; a start date alone searches that day.</p>
    {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    <button type="submit" className={REPORT_BUTTON}>Search</button>
  </form>;
}

export function InvoiceSearchResults({ initialParams }: { initialParams: InvoiceListParams }) {
  const [selected, setSelected] = useState<InvoiceListParams | null>(null);
  const params = selected ?? initialParams;
  return <div className="h-full min-h-0 overflow-auto">
    {selected && <button type="button" onClick={() => setSelected(null)} className="m-3 text-sm text-blue-600 dark:text-blue-400 hover:underline">Back to search results</button>}
    <InvoiceListContent key={JSON.stringify(params)} params={params} onSelect={setSelected} />
  </div>;
}
